import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'clsx';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { Text } from 'ui-components/components/text';

import { FulfillmentPlannerProperties } from '../../store/csw/fulfillment-planner-properties';
import Global from '../../store/global-selectors';
import { getDeliveryOptionsDynamoResults } from '../../delivery-options/delivery-options-api';
import { useEnableDeliveryOptionsDetails } from '../../feature-flags/hooks/use-enable-delivery-options-details';
import { useEnableThirdPartyDelivery } from '../../feature-flags/hooks/use-enable-third-party-delivery';
import { useIsShipperHqEnabledForStore } from '../../hooks/orders/use-shipperhq-check';
import { ShipperHQDetailsButton } from '../../orders/shipperhq-button';
import { ThirdPartyDeliveryOptionsButton } from '../../orders/third-pdo-button';
import messages from '../messages';
import RequestedServicePopover from './requested-service-popover';
import RequestedServiceModalLauncher from './requested-service-modal-launcher';
import { DeliveryOptionsContextProvider } from '../../pages/settings/stores/selected-store/delivery-options-settings/context';
import { deliveryOptionsQuoteIdentifier, shipperHqMarketplaceOrderId, ThirdPartyDeliveryOptionQuoteIdentifier } from './constants';
import style from './style.module.css';

const getQuoteId = (identifiers) => {
  const quoteId = identifiers.find((id) => id.key === deliveryOptionsQuoteIdentifier) || {};
  return quoteId.value;
};

const localizedUnspecifiedService = <FormattedMessage {...messages.unspecifiedService} />;

const RequestedShippingService = ({ className = null, fulfillmentPlanId }) => {
  const requestedShippingServiceName = useSelector((state) => {
    const fulfillmentPlannerProperties = new FulfillmentPlannerProperties({
      state,
      fulfillmentPlanId,
    });
    return fulfillmentPlannerProperties.requestedShippingServiceCode;
  });
  const requestedShippingServiceMapping = useSelector((state) => {
    const fulfillmentPlannerProperties = new FulfillmentPlannerProperties({
      state,
      fulfillmentPlanId,
    });
    return fulfillmentPlannerProperties.requestedShippingServiceMapping;
  });
  const isRequestedShippingServiceMapped = useMemo(
    () => requestedShippingServiceMapping.isMapped,
    [requestedShippingServiceMapping.isMapped],
  );

  const salesOrderId = useSelector((state) => {
    const fulfillmentPlannerProperties = new FulfillmentPlannerProperties({
      state,
      fulfillmentPlanId,
    });
    return fulfillmentPlannerProperties.salesOrderId;
  });
  const salesOrder = useSelector((state) => Global.selectEntitiesSalesOrderById(state, { salesOrderId })) || {};
  const { store = {} } = salesOrder;
  const storeGuid = store.storeGuid;
  const otherIdentifiers = salesOrder.otherIdentifiers || [];
  const quoteId = getQuoteId(otherIdentifiers);
  const shipperHqOrderNumber = otherIdentifiers.find((id) => id.key === shipperHqMarketplaceOrderId)?.value;
  const thirdPartyDeliveryNumber = otherIdentifiers.find((id) => id.key === ThirdPartyDeliveryOptionQuoteIdentifier)?.value;

  const styleClassNameForService = classNames(
    { [style['unspecified-service']]: !requestedShippingServiceName },
    { [style['specified-service']]: requestedShippingServiceName },
  );
  const isDeliveryOptionsDetailsEnabled = useEnableDeliveryOptionsDetails();
  const [deliveryOptions, setDeliveryOptions] = useState();

  useEffect(() => {
    if (quoteId) {
      const deliveryOptionsDetails = async () => {
        const resultData = await getDeliveryOptionsDynamoResults(quoteId, storeGuid);
        setDeliveryOptions(resultData);
      };
      deliveryOptionsDetails();
    }
  }, [storeGuid, quoteId]);

  const shouldShowDeliveryOptions = quoteId && deliveryOptions && !shipperHqOrderNumber && isDeliveryOptionsDetailsEnabled;

  const isTPDOEnabled = useEnableThirdPartyDelivery();
  const isFromShipperHqEnabledStore = useIsShipperHqEnabledForStore(storeGuid);
  const shouldShowShipperHQDetails = isFromShipperHqEnabledStore && !!shipperHqOrderNumber;
  const shouldShowTPDODetails = isTPDOEnabled && !!thirdPartyDeliveryNumber;
  return (
    <div className={classNames(className, style['requested-service'])}>
      <Text className={styleClassNameForService} shouldTruncate value={requestedShippingServiceName || localizedUnspecifiedService} />
      {requestedShippingServiceName && (
        <RequestedServiceModalLauncher isRequestedShippingServiceMapped={isRequestedShippingServiceMapped} />
      )}
      {shouldShowDeliveryOptions && (
        <DeliveryOptionsContextProvider storeGuid={storeGuid}>
          <RequestedServicePopover deliveryOptions={deliveryOptions} />
        </DeliveryOptionsContextProvider>
      )}
      {shouldShowShipperHQDetails && <ShipperHQDetailsButton orderNumber={shipperHqOrderNumber} storeGuid={storeGuid} />}
      {shouldShowTPDODetails && (
        <ThirdPartyDeliveryOptionsButton
          orderNumber={salesOrder.orderNumber}
          storeGuid={storeGuid}
          thirdPartyDeliveryNumber={thirdPartyDeliveryNumber}
        />
      )}
    </div>
  );
};

RequestedShippingService.propTypes = {
  className: PropTypes.string,
  fulfillmentPlanId: PropTypes.string.isRequired,
};

export default RequestedShippingService;
